import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const InvoiceManagement = () => {
  const [data, setData] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Fetch invoice data from API
  const fetchInvoices = async () => {
    try {
      const response = await fetch(
        "https://backend.jcgn.org/invoiceform/getAllInvoiceforms"
      );
      const responseData = await response.json();

      if (responseData.status) {
        // Map and handle null or empty values
        const modifiedData = responseData.result.map((item) => ({
          id: item.id,
          clientName: item.clientName || "N/A",
          email: item.email || "N/A",
          invoicenumber: item.invoicenumber || "N/A",
          organizationName: item.organizationName || "N/A",
          totalPrice: item.totalPrice || "0",
          date: item.date ? item.date.split("T")[0] : "N/A",
        }));

        // Sort data by descending ID
        modifiedData.sort((a, b) => b.id - a.id);

        setData(modifiedData);
      } else {
        console.error("Failed to fetch invoices:", responseData.message);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const loadLogoDataUrl = () =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Failed to load logo"));
      // High-res circular brand mark from public assets
      img.src = `${process.env.PUBLIC_URL || ""}/assets/logo2.png`;
    });

  const formatMoney = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) return String(value ?? "0");
    return `$${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Export table data to a branded official PDF
  const exportToPdf = async () => {
    if (!data.length) {
      Swal.fire("Warning!", "No invoice data to export.", "warning");
      return;
    }

    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const brandBlue = [13, 92, 171];
      const lightBlue = [232, 242, 252];
      const darkText = [33, 37, 41];
      const mutedText = [90, 98, 108];
      const generatedAt = new Date().toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Header band
      doc.setFillColor(...brandBlue);
      doc.rect(0, 0, pageWidth, 32, "F");

      try {
        const logoDataUrl = await loadLogoDataUrl();
        doc.addImage(logoDataUrl, "PNG", 12, 5, 22, 22);
      } catch (logoError) {
        console.warn("Invoice PDF logo skipped:", logoError);
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Jesus Center of Good News", 40, 13);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("A Community for All Nations  ·  Pray | Teach | Serve", 40, 19);
      doc.text("www.jcgn.org  ·  admin.jcgn.org", 40, 25);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("INVOICE SUMMARY REPORT", pageWidth - 14, 14, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(`Generated: ${generatedAt}`, pageWidth - 14, 21, { align: "right" });
      doc.text(`Total records: ${data.length}`, pageWidth - 14, 26, { align: "right" });

      // Accent line under header
      doc.setDrawColor(...brandBlue);
      doc.setLineWidth(0.6);
      doc.line(12, 36, pageWidth - 12, 36);

      doc.setTextColor(...mutedText);
      doc.setFontSize(9);
      doc.text(
        "Official invoice listing exported from the JCGN Admin Panel. For internal and authorized use only.",
        12,
        42
      );

      doc.autoTable({
        startY: 46,
        head: [
          [
            "Invoice ID",
            "Client Name",
            "Email",
            "Invoice Number",
            "Organization",
            "Total Price",
            "Date",
          ],
        ],
        body: data.map(
          ({
            id,
            clientName,
            email,
            invoicenumber,
            organizationName,
            totalPrice,
            date,
          }) => [
            id,
            clientName,
            email,
            invoicenumber,
            organizationName,
            formatMoney(totalPrice),
            date,
          ]
        ),
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 8,
          cellPadding: 2.5,
          textColor: darkText,
          lineColor: [210, 220, 230],
          lineWidth: 0.2,
          valign: "middle",
        },
        headStyles: {
          fillColor: brandBlue,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
          halign: "left",
        },
        alternateRowStyles: {
          fillColor: lightBlue,
        },
        columnStyles: {
          0: { cellWidth: 22 },
          5: { halign: "right", cellWidth: 28 },
          6: { cellWidth: 28 },
        },
        margin: { left: 12, right: 12, bottom: 18 },
        didDrawPage: (hookData) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageNumber = hookData.pageNumber;

          doc.setDrawColor(...brandBlue);
          doc.setLineWidth(0.4);
          doc.line(12, pageHeight - 12, pageWidth - 12, pageHeight - 12);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...mutedText);
          doc.text(
            "Jesus Center of Good News (JCGN)  ·  Confidential document",
            12,
            pageHeight - 7
          );
          doc.text(
            `Page ${pageNumber} of ${pageCount}`,
            pageWidth - 12,
            pageHeight - 7,
            { align: "right" }
          );
        },
      });

      doc.save(`JCGN_Invoice_Summary_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Error exporting invoices PDF:", error);
      Swal.fire("Error!", "Failed to export PDF. Please try again.", "error");
    }
  };

  // Handle delete invoice action
  const handleDeleteClick = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this invoice!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(
          `https://backend.jcgn.org/invoiceform/deleteInvoiceformByID/${id}`,
          { method: "DELETE" }
        )
          .then((response) => {
            if (!response.ok) throw new Error("Failed to delete invoice");

            setData(data.filter((item) => item.id !== id));
            Swal.fire("Deleted!", "The invoice has been deleted.", "success");
          })
          .catch((error) => {
            console.error("Error deleting invoice:", error);
            Swal.fire(
              "Error!",
              "Failed to delete invoice. Please try again later.",
              "error"
            );
          });
      }
    });
  };

  // Define table columns
  const columns = [
    { field: "id", headerName: "Invoice ID", width: 100 },
    { field: "clientName", headerName: "Client Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "invoicenumber", headerName: "Invoice Number", flex: 1 },
    { field: "organizationName", headerName: "Organization", flex: 1 },
    { field: "totalPrice", headerName: "Total Price", flex: 1 },
    { field: "date", headerName: "Date (YYYY/MM/DD)", flex: 1 },
    {
      field: "Actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <Link to={`/invoice/view/${params.row.id}`}>
              <IconButton>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="-10px"
      >
        <Header title="Invoice Management" subtitle="Managing invoices" />
      </Box>
      <Box
        m="10px 0 0 0"
        height="55vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "14px",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <Button
          variant="contained"
          onClick={exportToPdf}
          sx={{
            backgroundColor: "#4caf50",
            color: "white",
            fontSize: "10px",
            "&:hover": {
              backgroundColor: "#388e3c",
            },
          }}
        >
          Export as PDF
        </Button>
        <DataGrid
          rows={data}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
        />
      </Box>
    </Box>
  );
};

export default InvoiceManagement;
